import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { MapPin, Mail, Github, Linkedin, Code2, Send } from 'lucide-react';

const Contact = () => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseX = useSpring(x, { stiffness: 150, damping: 15 });
  const mouseY = useSpring(y, { stiffness: 150, damping: 15 });

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    const xPct = (clientX - left) / width - 0.5;
    const yPct = (clientY - top) / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  const rotateX = useTransform(mouseY, [-0.5, 0.5], ["8deg", "-8deg"]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], ["-8deg", "8deg"]);

  const socialLinks = [
    { icon: Github, name: "GitHub", url: "https://github.com/Pratik9113", color: "hover:text-white" },
    { icon: Linkedin, name: "LinkedIn", url: "https://www.linkedin.com/in/pratik-patil-b5681424b/", color: "hover:text-white" },
    { icon: Code2, name: "LeetCode", url: "https://leetcode.com/pratik9113_/", color: "hover:text-white" },
  ];

  return (
    <div className="min-h-screen bg-[#0b0f19] py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-white/5 rounded-full blur-[150px] -z-10 animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-black/5 rounded-full blur-[150px] -z-10 animate-pulse delay-1000" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="w-full max-w-2xl bg-[#111928]/40 backdrop-blur-3xl border border-white/5 rounded-[3rem] p-10 md:p-16 shadow-2xl relative group hover:border-white/20 transition-colors duration-700"
      >
        <div
          style={{ transform: "translateZ(60px)" }}
          className="relative z-10 flex flex-col items-center text-center"
        >
          <div className="mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-8">
              <Send className="h-4 w-4 text-white" />
              <span className="text-white text-[10px] font-black tracking-widest uppercase">Contact</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter gradient-text">
              Get in Touch
            </h2>
            <p className="text-gray-300 text-lg md:text-xl font-medium leading-relaxed max-w-lg mx-auto">
              Let's build the next generation of <span className="text-white font-bold">digital impact</span> together.
            </p>
          </div>

          <div className="space-y-6 w-full">
            <div className="flex items-center gap-6 p-6 rounded-[2.5rem] bg-[#0b0f19]/50 border border-white/5 hover:bg-[#0b0f19]/80 transition-all group/item hover:border-white/30">
              <div className="p-5 rounded-2xl bg-white/10 text-white group-hover/item:text-white group-hover/item:scale-110 transition-all shadow-inner">
                <Mail size={28} />
              </div>
              <div className="text-left overflow-hidden">
                <h3 className="text-white font-black text-xs tracking-widest uppercase mb-1 opacity-50">Direct Email</h3>
                <a href="mailto:pratik.patil9113@gmail.com" className="text-gray-200 text-base md:text-lg font-bold hover:text-white transition-colors break-all">
                  pratik.patil9113@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-6 p-6 rounded-[2.5rem] bg-[#0b0f19]/50 border border-white/5 hover:bg-[#0b0f19]/80 transition-all group/item hover:border-white/30">
              <div className="p-5 rounded-2xl bg-white/10 text-white group-hover/item:text-white group-hover/item:scale-110 transition-all shadow-inner">
                <MapPin size={28} />
              </div>
              <div className="text-left">
                <h3 className="text-white font-black text-xs tracking-widest uppercase mb-1 opacity-50">Location</h3>
                <p className="text-gray-200 text-base md:text-lg font-bold">Mumbai, Maharashtra, India</p>
              </div>
            </div>

            <div className="pt-10">
              <h3 className="text-white/30 font-black text-[10px] tracking-[0.4em] uppercase mb-8">Social Intelligence</h3>
              <div className="flex justify-center gap-6">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-4 bg-white/5 border border-white/10 rounded-2xl text-gray-300 transition-all duration-500 ${social.color} hover:bg-white/10 hover:border-white/40 hover:scale-110 shadow-lg`}
                  >
                    <social.icon size={26} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;